import React, { memo } from "react";
import imageNotFound from "../../assets/images/Image-not-found.png";

export const ProductSubCategoryTopbar = memo(function ProductSubCategoryTopbar({
  title,
  onBack,
}) {
  return (
    <div className="psc-topbar">
      <button type="button" className="psc-back-btn" aria-label="Back" onClick={onBack}>
        <i className="fas fa-angle-left" />
      </button>
      <div className="psc-title-stack">
        <span className="psc-topbar-title">{title}</span>
        <span className="psc-topbar-subtitle">Select a model by search or product attributes</span>
      </div>
    </div>
  );
});

export const ProductModelSearch = memo(function ProductModelSearch({
  searchTerm,
  searchResults,
  showDropdown,
  hasSelection,
  searchWrapRef,
  onSearchTermChange,
  onFocus,
  onClear,
  onResultClick,
}) {
  return (
    <div className="psc-search-wrap" ref={searchWrapRef}>
      <div className="psc-search-box">
        <i className="fas fa-search psc-search-icon" aria-hidden="true" />
        <input
          id="product-model-search"
          name="productModelSearch"
          type="text"
          className="psc-search-input"
          placeholder="Click to browse all models, or type to search..."
          value={searchTerm}
          onChange={(event) => onSearchTermChange(event.target.value)}
          onFocus={onFocus}
          onClick={onFocus}
        />
        {(hasSelection || searchTerm) && (
          <button type="button" className="psc-clear-btn" onClick={onClear}>
            Clear all
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="psc-search-dropdown">
          <div className="psc-search-dropdown-count">
            {searchTerm.trim().length > 0
              ? `${searchResults.length} model${searchResults.length === 1 ? "" : "s"} found`
              : `All ${searchResults.length} models - type to filter`}
          </div>
          <div className="psc-search-dropdown-list">
            {searchResults.length > 0 ? (
              searchResults.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  className="psc-search-item"
                  onClick={() => onResultClick(product)}
                >
                  <i className="fas fa-cube psc-search-item-icon" aria-hidden="true" />
                  <span className="psc-search-item-text">
                    <span className="psc-search-item-name">{product.productName}</span>
                    <span className="psc-search-item-sub">{product.subGroupName}</span>
                  </span>
                </button>
              ))
            ) : (
              <div className="psc-search-empty">No models match "{searchTerm}"</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export const ProductAttributeFilters = memo(function ProductAttributeFilters({
  filterGroups,
  onPillClick,
}) {
  return (
    <div className="psc-filters-col">
      {filterGroups.map(({ col, opts, selectedVal, isLocked }) => (
        <div
          key={col.key}
          className={`psc-filter-group${isLocked ? " psc-filter-group--locked" : ""}`}
        >
          <div className="psc-filter-label">
            {col.label}
            {isLocked && <i className="fas fa-lock psc-filter-lock-icon" aria-hidden="true" />}
          </div>
          <div className="psc-filter-pills">
            {opts.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`psc-pill${selectedVal === opt.value ? " psc-pill--active" : ""}${isLocked ? " psc-pill--disabled" : ""}`}
                disabled={isLocked}
                onClick={() => !isLocked && onPillClick(col, opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
});

export const ProductModelPreview = memo(function ProductModelPreview({
  matchedProduct,
  breadcrumbParts,
  hasSelection,
  matchCount,
  candidateProducts,
  onConfigure,
  onCandidateClick,
  highlightValues,
}) {
  if (matchedProduct) {
    const productImageSrc = matchedProduct.imageUrl || imageNotFound;

    return (
      <div className="psc-preview-card psc-preview-card--found">
        <div className="psc-preview-header">
          <div className="psc-preview-icon-wrap">
            <img
              src={productImageSrc}
              alt={matchedProduct.productName}
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = imageNotFound;
              }}
            />
          </div>
          <div className="psc-preview-name">{matchedProduct.productName}</div>
        </div>

        <div className="psc-preview-specs">
          {matchedProduct.productSpecGroupMaps?.length ? (
            matchedProduct.productSpecGroupMaps.flatMap((group) =>
              [...(group.productSpecMaps || [])]
                .sort((a, b) => a.sequence - b.sequence)
                .map((spec) => (
                  <div className="psc-spec-row" key={spec.id}>
                    <span className="psc-spec-label">{spec.attributeName}</span>
                    <span className="psc-spec-value">
                      {(() => {
                        const sortedValues = [...(spec.productSpecValueMaps || [])].sort(
                          (a, b) => a.sequence - b.sequence
                        );
                        const selectedValue = highlightValues?.[spec.attributeName];
                        return sortedValues.map((value, idx) => (
                          <React.Fragment key={value.id ?? value.valueName}>
                            <span
                              className={
                                selectedValue && value.valueName === selectedValue
                                  ? "psc-spec-value--selected"
                                  : undefined
                              }
                            >
                              {value.valueName}
                            </span>
                            {idx < sortedValues.length - 1 && ", "}
                          </React.Fragment>
                        ));
                      })()}
                    </span>
                  </div>
                ))
            )
          ) : (
            <div className="psc-spec-chips">
              {breadcrumbParts.map((part) => (
                <span className="psc-spec-chip" key={part}>
                  {part}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="psc-preview-footer">
          <button type="button" className="psc-configure-btn" onClick={onConfigure}>
            Configure
            <i className="fas fa-arrow-right" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="psc-preview-card psc-preview-card--empty">
      <div className="psc-preview-icon-wrap psc-preview-icon-wrap--muted">
        <i className="fas fa-cube psc-preview-icon" aria-hidden="true" />
      </div>
      <div className="psc-preview-hint-title">
        {hasSelection ? "Keep refining" : "Start selecting"}
      </div>
      <div className="psc-preview-hint-text">
        {hasSelection
          ? `${matchCount} model${matchCount === 1 ? "" : "s"} still match - narrow down further`
          : "Choose options on the left or search above"}
      </div>

      {candidateProducts.length > 0 && (
        <div className="psc-candidate-grid">
          {candidateProducts.map((product) => (
            <button
              key={product.id}
              type="button"
              className="psc-candidate-chip"
              onClick={() => onCandidateClick(product)}
            >
              {product.productName}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
